import { useState } from "react";
import { createFileRoute, Link, Outlet, useLocation } from "@tanstack/react-router";
import { api } from "~/common/utils/trpc.js";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Menu, Zap } from "lucide-react";
import { Button } from "@acme/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@acme/ui/sheet";
import { PlayerThBadge } from "../_components/-player-th-badge.js";
import { PlayerLevelBadge } from "../_components/-player-level-badge.js";
import { PlayerTrophiesBadge } from "../_components/-player-trophies-badge.js";
import { cn } from "@acme/ui";
import type { FileRoutesByTo } from "~/routeTree.gen.js";

export const Route = createFileRoute("/_authed/my-players/$tag")({
  component: PlayerTagLayout,
});

const TAB_ROUTES = [
  { to: "/my-players/$tag", label: "Dashboard", end: true },
  { to: "/my-players/$tag/clans", label: "Clans", end: false },
  { to: "/my-players/$tag/search-filter", label: "Search & filter", end: false },
  { to: "/my-players/$tag/matchs", label: "Matches", end: false },
  { to: "/my-players/$tag/social-media", label: "Social media", end: false },
  { to: "/my-players/$tag/notifications", label: "Notifications", end: false },
] as const satisfies { to: keyof FileRoutesByTo; label: string; end: boolean }[];

function TabLinks({
  tag,
  isActive,
  className,
  onNavigate,
}: {
  readonly tag: string;
  readonly isActive: (to: string, end: boolean) => boolean;
  readonly className?: string;
  readonly onNavigate?: () => void;
})
{
  return (
    <ul className={cn("flex flex-col gap-1 md:flex-row md:flex-wrap md:gap-1", className)}>
      {TAB_ROUTES.map(({ to, label, end }) =>
      {
        const active = isActive(to, end);
        return (
          <li key={to}>
            <Link
              to={to}
              params={{ tag }}
              onClick={onNavigate}
              className={cn(
                "inline-flex items-center px-3 py-2 text-sm font-medium rounded-md border-b-2 md:rounded-t-md transition-colors",
                active
                  ? "border-primary bg-muted/50"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/30"
              )}>
              {label}
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

function PlayerTagLayout()
{
  const { tag } = Route.useParams();
  const location = useLocation();
  const pathname = location.pathname;
  const [sheetOpen, setSheetOpen] = useState(false);

  const overviewQueryOptions = api.player.getOverviewItemByTag.queryOptions(
    { playerTag: tag },
    { enabled: tag.length > 0 }
  );
  const { data: overview } = useQuery(overviewQueryOptions);

  const displayTag = tag.startsWith("#") ? tag : `#${tag}`;

  const isActive = (to: string, end: boolean) =>
  {
    const path = to.replace("$tag", tag);
    if (end) return pathname === path || pathname === path + "/";
    return pathname.startsWith(path);
  };

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border pb-4 mb-6">
        <Link
          to="/my-players"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground shrink-0">
          <ArrowLeft className="h-4 w-4" />
          My Players
        </Link>
        <nav aria-label="Player sections" className="flex items-center min-w-0">
          <div className="hidden md:block">
            <TabLinks tag={tag} isActive={isActive} />
          </div>
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden shrink-0" aria-label="Open menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[min(100vw-2rem,18rem)]">
              <SheetHeader>
                <SheetTitle>Player sections</SheetTitle>
              </SheetHeader>
              <TabLinks
                tag={tag}
                isActive={isActive}
                className="flex-col! pt-2"
                onNavigate={() => setSheetOpen(false)}
              />
            </SheetContent>
          </Sheet>
        </nav>
      </div>

      {overview != null ? (
        <div className="mb-6 flex flex-col gap-2">
          <h1 className="flex items-center gap-2 text-2xl sm:text-3xl font-bold truncate">
            {overview.name}
            <Zap className="size-6 shrink-0 text-amber-500" aria-hidden />
          </h1>
          <p className="text-sm text-muted-foreground font-mono">{displayTag}</p>
          <div className="flex flex-wrap gap-2">
            <PlayerThBadge level={overview.townHallLevel} />
            <PlayerLevelBadge level={overview.expLevel} />
            <PlayerTrophiesBadge trophies={overview.trophies} leagueId={overview.league} />
          </div>
        </div>
      ) : (
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-muted-foreground">{displayTag}</h1>
        </div>
      )}

      <Outlet />
    </div>
  );
}
